import { Navigate, useLocation } from "react-router-dom";
import { useOrgContext } from "../context/OrgContext";
import { useAuth } from "../hooks/useAuth";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { activeOrg, loading: orgLoading } = useOrgContext();

  if (isLoading || orgLoading) {
    // Show loading state while checking authentication or org membership
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-indigo-50/50 flex items-center justify-center">
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-[600px] h-[600px] bg-gradient-to-br from-violet-200/50 via-indigo-100/40 to-transparent rounded-full blur-3xl" />
          <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-gradient-to-tr from-cyan-100/50 via-blue-100/30 to-transparent rounded-full blur-3xl" />
        </div>
        <div className="relative flex flex-col items-center">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-violet-100"></div>
            <div className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-violet-500 animate-spin"></div>
          </div>
          <p className="mt-6 text-slate-700 text-base font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Synchronous redirect — never render children when there is no org, so
  // hooks inside App (useSettings, useAnalysisResults) never fire API calls
  // before the user has completed onboarding.
  if (
    !activeOrg &&
    !location.pathname.startsWith("/onboarding") &&
    !location.pathname.startsWith("/join")
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
